import { useEffect, useRef, useState } from "react";
import type { BasketController } from "./basket";
import { copyText, formatBasketItem, toBasketItem } from "./basket";
import { CaseTextBody } from "./case-reader";
import { formatCaseCitation, formatDecisionDate } from "./local-provider";
import { EvidenceKindChip, EvidenceList } from "./results";
import type { CaseTextDocument, SearchProvider, SearchResult } from "./types";

type ReaderState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "ready"; document: CaseTextDocument }
  | { phase: "missing" }
  | { phase: "error" };

/** 상세 패널 안에서만 선택된 텍스트를 인용문으로 읽는다. */
function selectionWithin(root: HTMLElement | null): string | undefined {
  if (!root || typeof window === "undefined") return undefined;
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return undefined;
  const text = selection.toString().trim();
  if (!text) return undefined;
  const anchor = selection.anchorNode;
  if (!anchor || !root.contains(anchor)) return undefined;
  return text;
}

function caseMeta(result: SearchResult): string {
  return [
    result.court,
    formatDecisionDate(result.decisionDate),
    result.caseNo,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function DetailPane({
  result,
  provider,
  basket,
  onOpenSetup,
}: {
  result: SearchResult | null;
  provider?: SearchProvider;
  basket: BasketController;
  onOpenSetup?: () => void;
}) {
  const [state, setState] = useState<ReaderState>({ phase: "idle" });
  const bodyRef = useRef<HTMLDivElement>(null);
  const [copyState, setCopyState] = useState<"idle" | "done" | "fail">("idle");
  const pointer = result?.pointer;
  const isCase = Boolean(result?.caseNo);

  useEffect(() => {
    setState({ phase: "idle" });
    if (!pointer || !isCase) return;

    let cancelled = false;
    setState({ phase: "loading" });

    if (!provider?.caseText) {
      setState({ phase: "missing" });
      return () => {
        cancelled = true;
      };
    }

    void provider
      .caseText(pointer)
      .then((document) => {
        if (cancelled) return;
        if (document.status === "available" && document.text !== undefined) {
          setState({ phase: "ready", document });
        } else if (document.status === "unavailable") {
          setState({ phase: "error" });
        } else {
          setState({ phase: "missing" });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ phase: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [provider, pointer, isCase]);

  if (!result) {
    return (
      <div className="ws-detail-empty">
        <p className="ws-detail-empty-title">결과를 선택하세요</p>
        <p className="ws-detail-empty-sub">
          왼쪽 목록에서 조문이나 판례를 고르면 이곳에 전문이 표시됩니다.
        </p>
      </div>
    );
  }

  const item = toBasketItem(result);
  const added = basket.has(result.pointer);
  const meta = isCase ? caseMeta(result) : result.hierarchy;
  const title = isCase
    ? (result.citation ??
      formatCaseCitation({
        pointer: result.pointer,
        court: result.court,
        caseNo: result.caseNo,
        decisionDate: result.decisionDate,
      }))
    : (result.label ?? result.pointer);

  const add = () => {
    basket.add(result, selectionWithin(bodyRef.current));
  };

  const copyOne = () => {
    const text = formatBasketItem(toBasketItem(result, selectionWithin(bodyRef.current)));
    void copyText(text).then((ok) => {
      setCopyState(ok ? "done" : "fail");
      setTimeout(() => setCopyState("idle"), ok ? 1400 : 1800);
    });
  };

  return (
    <div className="ws-detail-pane">
      <header className="ws-detail-head">
        <div className="ws-detail-kicker">
          <span className={`kind kind-${item.kind}`} aria-hidden="true">
            {item.kind === "statute" ? "법" : "판"}
          </span>
          <span className="ws-detail-kicker-label">
            {isCase ? (result.evidence.statutes[0] ?? "판례") : "법조문"}
          </span>
          {meta ? <span className="ws-detail-meta">{meta}</span> : null}
        </div>
        <h2 className="ws-detail-title">{title}</h2>
        <div className="ws-detail-actions">
          <button
            type="button"
            className={`basket-add${added ? " is-added" : ""}`}
            onClick={add}
            aria-pressed={added}
          >
            {added ? "담김" : "담기"}
          </button>
          <button type="button" className="basket-copy-one" onClick={copyOne}>
            {copyState === "done" ? "복사됨" : copyState === "fail" ? "복사 실패" : "인용 복사"}
          </button>
        </div>
      </header>

      <div className="ws-detail-scroll">
        <div className="ws-detail-body" ref={bodyRef}>
          {result.evidenceSentences && result.evidenceSentences.length > 0 ? (
            <div className="ws-detail-evidence-kinds">
              {result.evidenceSentences.some((sentence) => sentence.kind === "citation") ? (
                <EvidenceKindChip kind="citation" />
              ) : null}
              {result.evidenceSentences.some((sentence) => sentence.kind === "related") ? (
                <EvidenceKindChip kind="related" />
              ) : null}
            </div>
          ) : null}

          {!isCase ? <p className="ws-detail-text">{result.text}</p> : null}

          {isCase && state.phase === "loading" ? (
            <p className="ws-detail-note">판례 전문을 불러오는 중…</p>
          ) : null}

          {isCase && state.phase === "ready" ? (
            <>
              {state.document.sections ? (
                <p className="ws-detail-count">전문 {state.document.sections}개 섹션</p>
              ) : null}
              <CaseTextBody
                text={state.document.text ?? ""}
                evidenceSentences={result.evidenceSentences}
                className="ws-detail-text"
              />
            </>
          ) : null}

          {isCase && state.phase === "missing" ? (
            <div className="ws-detail-missing">
              <p className="ws-detail-missing-title">판례 전문 볼륨이 설치되지 않았습니다</p>
              <p className="ws-detail-missing-detail">
                판례 본문 볼륨을 설치하면 이 판례의 전문을 볼 수 있습니다.
              </p>
              {onOpenSetup ? (
                <button type="button" className="case-reader-install" onClick={onOpenSetup}>
                  설정에서 설치
                </button>
              ) : null}
              <p className="ws-detail-text">{result.text}</p>
            </div>
          ) : null}

          {isCase && state.phase === "error" ? (
            <>
              <p className="ws-detail-note">판례 전문을 불러오지 못했습니다.</p>
              <p className="ws-detail-text">{result.text}</p>
            </>
          ) : null}

          <EvidenceList evidence={result.evidence} />
          <p className="ws-detail-hint">
            본문을 선택한 뒤 담기하면 인용문이 함께 저장됩니다. ⌘/Ctrl+Enter로 바구니를 복사합니다.
          </p>
        </div>
      </div>
    </div>
  );
}

export function BasketBar({
  basket,
  wide,
}: {
  basket: BasketController;
  wide: boolean;
}) {
  const count = basket.items.length;
  if (!wide && count === 0) return null;
  const copyLabel =
    basket.copyState === "done"
      ? "복사됨"
      : basket.copyState === "fail"
        ? "복사 실패"
        : "전체 복사";

  return (
    <div className={wide ? "basket-bar" : "basket-pill"} role="region" aria-label="인용 바구니">
      <span className="basket-count">인용 {count}건</span>
      <button
        type="button"
        className="basket-action basket-action-primary"
        onClick={() => void basket.copy()}
        disabled={count === 0}
      >
        {copyLabel}
      </button>
      <button
        type="button"
        className="basket-action"
        onClick={basket.clear}
        disabled={count === 0}
      >
        비우기
      </button>
    </div>
  );
}
